import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { AppConfig } from '~/core/models/appconfig';

// Priority order: Try browser/assets/config.json first (deployment path for all envs)
// Fallback to assets/config.json if browser path doesn't exist
const FILES = ["browser/assets/config.json", "assets/config.json"];
const LOCAL_FILE = 'assets/setlocal.json';

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private config: AppConfig = new AppConfig();
  private http: HttpClient;

  constructor(private httpHandler: HttpBackend) {
    this.http = new HttpClient(this.httpHandler);
  }

  public async init(): Promise<boolean> {
    try {
      let configData: AppConfig | null = null;

      // Try each config file path until one succeeds
      // Priority: browser/assets/config.json (deployment), then assets/config.json (dev fallback)
      for (const file of FILES) {
        try {
          const config$ = this.http.get<AppConfig>(file);
          configData = await lastValueFrom(config$);
          break; // Success, exit loop
        } catch (e) {
          console.warn(`Failed to load config from ${file}, trying next...`);
        }
      }

      if (!configData) {
        throw new Error('Failed to load config from any path');
      }

      // Try to load local override config (optional, for local dev)
      let localConfig: Partial<AppConfig> | null = null;
      try {
        const local$ = this.http.get<Partial<AppConfig>>(LOCAL_FILE);
        localConfig = await lastValueFrom(local$);
        console.log('Loaded local config override:', localConfig);
      } catch (e) {
        // Local config is optional
      }

      // Merge local config if available
      this.config = { ...configData, ...(localConfig || {}) };

      console.log('[AppConfigService.init] Config loaded:', {
        environment: this.config.environment,
        hasApiUrl: !!this.config.apiUrl,
        hasOutboundApiUrl: !!this.config.outboundApiUrl,
        hasBaseUrl: !!this.config.baseUrl
      });

      // If URLs are missing from config, try to load from environment-specific config
      if (!this.config.apiUrl || !this.config.outboundApiUrl || !this.config.baseUrl) {
        await this.loadEnvironmentUrls();
      }

      return true;
    } catch (error) {
      console.error('Error loading config:', error);
      throw error;
    }
  }

  private async loadEnvironmentUrls(): Promise<void> {
    try {
      const environment = this.config.environment || 'prod';
      
      try {
        // Dynamically import the environment file based on environment value
        let environmentConfig: any;
        
        switch (environment.toLowerCase()) {
          case 'dev':
            environmentConfig = (await import('../../../environments/environment.dev')).environment;
            break;
          case 'test':
            environmentConfig = (await import('../../../environments/environment.test')).environment;
            break;
          case 'prod':
            environmentConfig = (await import('../../../environments/environment.prod')).environment;
            break;
          default:
            environmentConfig = (await import('../../../environments/environment.prod')).environment;
            break;
        }
        
        // Merge environment-specific URLs, preserving existing values
        if (environmentConfig.apiUrl && !this.config.apiUrl) {
          this.config.apiUrl = environmentConfig.apiUrl;
        }
        if (environmentConfig.outboundApiUrl && !this.config.outboundApiUrl) {
          this.config.outboundApiUrl = environmentConfig.outboundApiUrl;
        }
        if (environmentConfig.baseUrl && !this.config.baseUrl) {
          this.config.baseUrl = environmentConfig.baseUrl;
        }
        if (environmentConfig.wellsApiKey && !this.config.wellsApiKey) {
          this.config.wellsApiKey = environmentConfig.wellsApiKey;
        }
        
        console.log(`Loaded URLs from environment.${environment}.ts`);
      } catch (e) {
        console.warn(`Failed to load URLs from environment.${environment}.ts`, e);
      }
    } catch (error) {
      console.warn('Could not load environment-specific URLs:', error);
    }
  }

  public getConfig(key?: string | string[]): any {
    if (!key || (Array.isArray(key) && !key[0])) {
      return this.config;
    }

    const path = Array.isArray(key) ? key : key.split('.');
    return path.reduce((acc: any, current: string) => acc?.[current], this.config);
  }

  /**
   * Get API URL with fallback logic
   * Returns config.apiUrl if available, or attempts to get from environment config
   */
  public getApiUrl(): string {
    if (this.config.apiUrl) {
      return this.config.apiUrl;
    }
    
    // Fallback: try to get from window or environment
    if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.apiUrl) {
      return (window as any).APP_CONFIG.apiUrl;
    }
    
    console.warn('API URL not configured in config or environment');
    return '';
  }

  /**
   * Get Outbound API URL with fallback logic
   */
  public getOutboundApiUrl(): string {
    if (this.config.outboundApiUrl) {
      return this.config.outboundApiUrl;
    }
    
    if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.outboundApiUrl) {
      return (window as any).APP_CONFIG.outboundApiUrl;
    }
    
    console.warn('Outbound API URL not configured in config or environment');
    return '';
  }

  /**
   * Get Base URL with fallback logic
   */
  public getBaseUrl(): string {
    if (this.config.baseUrl) {
      return this.config.baseUrl;
    }
    
    if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.baseUrl) {
      return (window as any).APP_CONFIG.baseUrl;
    }
    
    // Fallback to current window location
    return window?.location?.origin || '';
  }

  /**
   * Get Wells API Key with fallback logic
   */
  public getWellsApiKey(): string {
    if (this.config.wellsApiKey) {
      return this.config.wellsApiKey;
    }
    
    if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.wellsApiKey) {
      return (window as any).APP_CONFIG.wellsApiKey;
    }
    
    return '';
  }
}